import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Page, BlockCard, DropdownCard, Button, Select, Tag } from '@trellixio/roaster-coffee';
import Locales from 'locales';
import { useQuery } from '@tanstack/react-query';
import { useAPI } from 'hooks';
import { formatDate } from 'helpers';
import { StatCardsList } from 'components';
import TripDistributionChart from './TripDistributionChart';
import TripDistributionTable from './TripDistributionTable';
import style from './AGLODashboard.module.css';

const CONST_DISPLAY_DURATION = 1000;
const CONST_NONE = 'none';
const CONST_FILTER_LABEL_YEAR = 'year';

const AGLODashboard = ({
  queryFilters,
  setQueryFilter,
  yearsArray,
  preSetYear,
  setSearchParams,
  selectedYear,
  setYear,
  showTag,
  setShowTag,
  fetchParams,
}) => {
  const { api } = useAPI();

  const { data: reportsData } = useQuery({
    queryFn: api.list('trip/trip_report/', fetchParams),
    queryKey: ['trip/trip_report/', queryFilters],
  });

  const { data: pilgrimsStatsData } = useQuery({
    queryFn: api.list('trip/stats/default/', fetchParams),
    queryKey: ['trip/stats/default/', queryFilters],
  });

  const { data: pilgrimsCategoryStatsData } = useQuery({
    queryFn: api.list('trip/stats/category/', fetchParams),
    queryKey: ['trip/stats/category/', queryFilters],
  });

  const { data: pilgrimsCategoryPaidStatsData } = useQuery({
    queryFn: api.list('trip/stats/trip_category_paid/', fetchParams),
    queryKey: ['trip/stats/trip_category_paid/', queryFilters],
  });

  const { data: agenciesStatsData } = useQuery({
    queryFn: api.list('agency/stats/default/', fetchParams),
    queryKey: ['agency/stats/default/', queryFilters],
  });

  const reports = reportsData?.data;

  const pilgrimsStats = pilgrimsStatsData?.data.data;

  const agenciesStats = agenciesStatsData?.data.data;

  const pilgrimsCategoryStats = pilgrimsCategoryStatsData?.data.data;

  const pilgrimsCategoryPaidStats = pilgrimsCategoryPaidStatsData?.data.data;

  const [lastDownloadTime, setLastDownloadTime] = useState({
    chart: null,
    table: null,
  });

  // Updates the last download time for a specific section and resets this value after a defined duration.
  const updateDownloadTime = (key) => {
    // Create a timestamp with the current date and time
    const timestamp = formatDate(new Date(), 'dddd DD MMMM YYYY [à] HH:mm:ss');

    // Update the state to display the timestamp for the corresponding section
    setLastDownloadTime((prev) => ({ ...prev, [key]: timestamp }));

    // Start a timeout to reset the timestamp after the defined duration
    setTimeout(() => {
      setLastDownloadTime((prev) => ({ ...prev, [key]: null }));
    }, CONST_DISPLAY_DURATION);
  };

  // Function to hide the download button
  const hideDownloadButton = (container) => {
    const downloadButton = container.querySelector('.button.secondary');
    if (downloadButton) {
      downloadButton.style.display = 'none';
    }
  };

  // Function to print the reports
  const printAction = (sectionRef) => {
    const printContent = sectionRef.current?.cloneNode(true);

    // Hide the download button before printing
    hideDownloadButton(printContent);

    // Open a new window for printing
    const printWindow = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');

    // Copy styles from the main document
    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules)
            .map((rule) => rule.cssText)
            .join('');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    const reportStyles = `
     @media print {
        html {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
        }
        body {
          -webkit-print-color-adjust: exact; /* For Chrome and Edge */
          print-color-adjust: exact; /* For other browsers */
        }
        @page {
          size: landscape;
          margin: 1.3cm;
        }
      }
    `;

    // Include the DM Sans font via Google Fonts
    const fontLink = `<link href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,200;9..40,300;9..40,400&display=swap" rel="stylesheet" />`;

    printWindow.document.write(fontLink); // Include the font link

    // Write styles and content to the new window
    printWindow.document.write(`<style>${styles}${reportStyles}</style>`);
    printWindow.document.write(printContent.outerHTML);
    printWindow.document.close();

    // Focus on the new window and initiate printing
    printWindow.focus();
    printWindow.print();
  };

  // Function to trigger printing and update the timestamp
  const handlePrint = (sectionRef, sectionKey) => {
    updateDownloadTime(sectionKey); // Update the timestamp
    setTimeout(() => {
      printAction(sectionRef); // Print the section
    }, 500);
  };

  const applyFilters = () => {
    let filters = null;

    if (selectedYear !== CONST_NONE) {
      filters = { [CONST_FILTER_LABEL_YEAR]: selectedYear };
    }

    setQueryFilter(filters);
    setShowTag(true);
  };

  const resetFilters = () => {
    setYear(CONST_NONE);
    setQueryFilter(null);
    setShowTag(false);
  };

  useMemo(() => {
    // set value if in URL and allow the filter to be applied only if the user have the right to apply it
    if (preSetYear !== CONST_NONE) {
      applyFilters();
    }
  }, []);

  useMemo(() => {
    if (preSetYear === null) {
      resetFilters();
    }
  }, [preSetYear]);

  useMemo(() => {
    let params = {};

    // update URL each time the filter is selected
    if (selectedYear !== CONST_NONE) {
      params = { [CONST_FILTER_LABEL_YEAR]: selectedYear };
    }

    if (params) {
      setSearchParams(params);
    }
  }, [selectedYear]);

  return (
    <Page
      title={Locales.dashboard.title}
      size="full"
      className={style['big-container']}
      secondaryAction={
        <form>
          <DropdownCard>
            <DropdownCard.Trigger>
              <Button variant="secondary">
                <i className="fa-regular fa-bars-filter" /> {Locales.trip.filter.filter}
              </Button>
            </DropdownCard.Trigger>
            <DropdownCard.Dropdown width="large">
              <DropdownCard.Content>
                <form>
                  <div className="content-row">
                    <p className="group-label">{Locales.campaign.dropdown.campaign_year}</p>
                    <Select
                      defaultValue={selectedYear}
                      helpText={Locales.campaign.dropdown.campaign_year_helptext}
                      data={[{ label: Locales.base.button.select, value: CONST_NONE, disabled: true }].concat(
                        yearsArray
                      )}
                      onChange={setYear}
                    />
                  </div>
                </form>
              </DropdownCard.Content>
              <DropdownCard.Actions>
                <Button variant="inline" onClick={resetFilters}>
                  {Locales.base.button.reset}
                </Button>
                <Button color="success" onClick={applyFilters}>
                  {Locales.base.button.validate}
                </Button>
              </DropdownCard.Actions>
            </DropdownCard.Dropdown>
          </DropdownCard>
        </form>
      }
    >
      {showTag && (
        <BlockCard>
          <div className="table-meta items-group">
            <div className="tags-container">
              {queryFilters && Object.prototype.hasOwnProperty.call(queryFilters, CONST_FILTER_LABEL_YEAR) && (
                <Tag
                  onRemove={() => resetFilters()}
                >{`${Locales.campaign.dropdown.campaign_year}: ${selectedYear}`}</Tag>
              )}
            </div>
          </div>
        </BlockCard>
      )}

      <div className="stats-container items-group wrap xl">
        {pilgrimsStats && <StatCardsList statsData={pilgrimsStats} />}
        {pilgrimsCategoryStats && <StatCardsList statsData={pilgrimsCategoryStats} />}
        {pilgrimsCategoryPaidStats && <StatCardsList statsData={pilgrimsCategoryPaidStats} />}
        {agenciesStats && <StatCardsList statsData={agenciesStats} />}
      </div>

      <div className={style['reports-block']}>
        <TripDistributionChart reports={reports} lastDownloadTime={lastDownloadTime.chart} handlePrint={handlePrint} />

        <TripDistributionTable reports={reports} lastDownloadTime={lastDownloadTime.table} handlePrint={handlePrint} />
      </div>
    </Page>
  );
};

AGLODashboard.propTypes = {
  queryFilters: PropTypes.shape({}),
  setQueryFilter: PropTypes.func,
  yearsArray: PropTypes.arrayOf(PropTypes.shape({})),
  preSetYear: PropTypes.string,
  setSearchParams: PropTypes.func,
  selectedYear: PropTypes.string,
  setYear: PropTypes.func,
  showTag: PropTypes.bool,
  setShowTag: PropTypes.func,
  fetchParams: PropTypes.shape({}),
};

AGLODashboard.defaultProps = {
  queryFilters: null,
  setQueryFilter: null,
  yearsArray: null,
  preSetYear: null,
  setSearchParams: null,
  selectedYear: null,
  setYear: null,
  showTag: false,
  setShowTag: null,
  fetchParams: null,
};

export default AGLODashboard;
